/**
 * LICENSE PLANS REACT QUERY HOOKS
 *
 * React Query hooks for managing license plans in super admin dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLicenseService } from '@/services/adminLicenseService';
import { useToast } from '@/hooks/use-toast';
import type { LicensePlan } from '@repo/types';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Get all license plans
 */
export function useLicensePlans() {
  return useQuery({
    queryKey: ['license-plans'],
    queryFn: () => AdminLicenseService.getAllPlans(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get single plan by ID
 */
export function useLicensePlan(id: string) {
  return useQuery({
    queryKey: ['license-plan', id],
    queryFn: () => AdminLicenseService.getPlanById(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Create new license plan
 */
export function useCreatePlan() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (plan: Omit<LicensePlan, 'id' | 'createdAt' | 'updatedAt'>) =>
      AdminLicenseService.createPlan(plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['license-plans'] });
      toast({
        title: '✅ Plan Created',
        description: 'New license plan has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Creating Plan',
        description: error.message,
      });
    },
  });
}

/**
 * Update existing license plan
 */
export function useUpdatePlan(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (updates: Partial<LicensePlan>) =>
      AdminLicenseService.updatePlan(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['license-plans'] });
      queryClient.invalidateQueries({ queryKey: ['license-plan', id] });
      toast({
        title: '✅ Plan Updated',
        description: 'License plan has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Updating Plan',
        description: error.message,
      });
    },
  });
}

/**
 * Toggle plan active status
 */
export function useTogglePlanStatus(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (isActive: boolean) =>
      AdminLicenseService.togglePlanStatus(id, isActive),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['license-plans'] });
      queryClient.invalidateQueries({ queryKey: ['license-plan', id] });
      toast({
        title: data.is_active ? '✅ Plan Activated' : '⚠️ Plan Deactivated',
        description: `Plan "${data.name}" is now ${data.is_active ? 'active' : 'inactive'}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Toggling Plan Status',
        description: error.message,
      });
    },
  });
}
